import React from 'react';
import {
	PieChart,
	Pie,
	Cell,
	Tooltip,
	ResponsiveContainer,
	XAxis,
	YAxis,
	CartesianGrid,
	AreaChart,
	Area,
} from 'recharts';
import {
	Box,
	Card,
	CardHeader,
	CardBody,
	Grid,
	Heading,
	Text,
	HStack,
	VStack,
} from '@chakra-ui/react';

// ── Colour palette ─────────────────────────────────────────────────────────────
const PALETTE = {
	teal: '#0694a2',
	green: '#0e9f6e',
	purple: '#8b5cf6',
	red: '#f05252',
	orange: '#ff8c00',
	blue: '#3b82f6',
	pink: '#ec4899',
};

// ── Custom tooltip shared style ────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
	if (!active || !payload?.length) return null;
	return (
		<Box
			bg='white'
			border='1px solid'
			borderColor='gray.200'
			rounded='md'
			px={3}
			py={2}
			shadow='md'>
			{label && (
				<Text fontSize='xs' fontWeight='bold' color='gray.600' mb={1}>
					{label}
				</Text>
			)}
			{payload.map((p, i) => (
				<HStack key={i} spacing={2}>
					<Box w={2} h={2} rounded='full' bg={p.fill || p.color || 'gray.400'} />
					<Text fontSize='xs' color='gray.700'>
						{p.name}: <strong>{p.value?.toLocaleString()}</strong>
					</Text>
				</HStack>
			))}
		</Box>
	);
};

// ── Legend item for donut charts ───────────────────────────────────────────────
const LegendItem = ({ color, label, value }) => (
	<HStack spacing={2} align='center'>
		<Box w={3} h={3} rounded='full' flexShrink={0} bg={color} />
		<Text fontSize='xs' color='gray.600' noOfLines={1}>
			{label}
		</Text>
		{value !== undefined && (
			<Text fontSize='xs' fontWeight='bold' color='gray.800' ml='auto'>
				{value.toLocaleString()}
			</Text>
		)}
	</HStack>
);

// ════════════════════════════════════════════════════════════════════════════════
const DashboardCharts = ({ analytics, campaigns }) => {
	// ── 1. Email engagement donut ──────────────────────────────────────────────
	const engagementRaw = [
		{
			name: 'Delivered',
			value: analytics?.totalEmailsDelivered || 0,
			color: PALETTE.teal,
		},
		{ name: 'Opened', value: analytics?.totalOpens || 0, color: PALETTE.green },
		{ name: 'Clicked', value: analytics?.totalClicks || 0, color: PALETTE.blue },
		{ name: 'Bounced', value: analytics?.totalBounces || 0, color: PALETTE.red },
		{
			name: 'Unsubscribed',
			value: analytics?.totalUnsubscribes || 0,
			color: PALETTE.orange,
		},
	].filter((d) => d.value > 0);

	const hasEngagement = engagementRaw.length > 0;

	// ── 2. Campaigns created per month (last 6 months) ─────────────────────────
	const now = new Date();
	const months = Array.from({ length: 6 }, (_, i) => {
		const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
		return {
			key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
			label: d.toLocaleString('default', { month: 'short', year: '2-digit' }),
		};
	});
	const campaignCountMap = (campaigns || []).reduce((acc, c) => {
		if (!c.createdAt) return acc;
		const d = new Date(c.createdAt);
		const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
		acc[key] = (acc[key] || 0) + 1;
		return acc;
	}, {});
	const monthlyData = months.map(({ key, label }) => ({
		month: label,
		campaigns: campaignCountMap[key] || 0,
	}));
	const hasMonthly = monthlyData.some((m) => m.campaigns > 0);

	// ── Empty state placeholder ────────────────────────────────────────────────
	const EmptyState = ({ label }) => (
		<Box display='flex' alignItems='center' justifyContent='center' h='180px'>
			<Text fontSize='sm' color='gray.400'>
				{label}
			</Text>
		</Box>
	);

	return (
		<VStack spacing={6} align='stretch'>
			<Heading size='md' color='gray.700'>
				Analytics Overview
			</Heading>

			<Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={6}>
				{/* ── Email Engagement Donut ─────────────────────────────────────── */}
				<Card shadow='sm' borderWidth='1px' borderColor='gray.100'>
					<CardHeader pb={2}>
						<Heading size='sm' color='gray.700'>
							Email Engagement
						</Heading>
						<Text fontSize='xs' color='gray.500' mt={1}>
							All-time breakdown across every campaign
						</Text>
					</CardHeader>
					<CardBody pt={0}>
						{hasEngagement ? (
							<Grid templateColumns='1fr 1fr' gap={4} alignItems='center'>
								<ResponsiveContainer width='100%' height={180}>
									<PieChart>
										<Pie
											data={engagementRaw}
											cx='50%'
											cy='50%'
											innerRadius={45}
											outerRadius={78}
											paddingAngle={3}
											dataKey='value'>
											{engagementRaw.map((entry, i) => (
												<Cell key={i} fill={entry.color} />
											))}
										</Pie>
										<Tooltip content={<CustomTooltip />} />
									</PieChart>
								</ResponsiveContainer>
								<VStack align='stretch' spacing={2}>
									{engagementRaw.map((d) => (
										<LegendItem
											key={d.name}
											color={d.color}
											label={d.name}
											value={d.value}
										/>
									))}
								</VStack>
							</Grid>
						) : (
							<EmptyState label='No email events tracked yet' />
						)}
					</CardBody>
				</Card>

				{/* ── Campaigns Created Over Time ────────────────────────────────── */}
				<Card shadow='sm' borderWidth='1px' borderColor='gray.100'>
					<CardHeader pb={2}>
						<Heading size='sm' color='gray.700'>
							Campaigns Created
						</Heading>
						<Text fontSize='xs' color='gray.500' mt={1}>
							Last 6 months
						</Text>
					</CardHeader>
					<CardBody pt={0}>
						{hasMonthly ? (
							<ResponsiveContainer width='100%' height={220}>
								<AreaChart
									data={monthlyData}
									margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
									<defs>
										<linearGradient id='gradCampaigns' x1='0' y1='0' x2='0' y2='1'>
											<stop offset='5%' stopColor={PALETTE.teal} stopOpacity={0.25} />
											<stop offset='95%' stopColor={PALETTE.teal} stopOpacity={0} />
										</linearGradient>
									</defs>
									<CartesianGrid strokeDasharray='3 3' stroke='#f0f0f0' />
									<XAxis dataKey='month' tick={{ fontSize: 11 }} />
									<YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
									<Tooltip content={<CustomTooltip />} />
									<Area
										type='monotone'
										dataKey='campaigns'
										stroke={PALETTE.teal}
										fill='url(#gradCampaigns)'
										strokeWidth={2}
										dot={{ r: 4, fill: PALETTE.teal }}
										activeDot={{ r: 6 }}
									/>
								</AreaChart>
							</ResponsiveContainer>
						) : (
							<EmptyState label='No campaigns in the last 6 months' />
						)}
					</CardBody>
				</Card>
			</Grid>
		</VStack>
	);
};

export default DashboardCharts;
